import type { BoundingBox } from '@rock/types';
import { isQuarterTurn, normalizeRotation } from '@rock/util';

/**
 * How to paint one image inside a fixed SQUARE tile, in both of the gallery's states: cropped to fill
 * the tile at rest, and shrunk to show the whole image on hover. Both states share one `<img>` box —
 * only the transform differs — so the browser can tween between them on the compositor.
 */
export type ThumbnailFraming = {
    /** Rotation to apply, in degrees, normalized to [0, 360). */
    rotationDeg: number;
    /** The `<img>` box BEFORE rotation, sized so the rotated image exactly fits inside the tile. */
    width: number;
    height: number;
    /** Uniform scale that grows that fitted image until it covers the tile. 1 for a square source. */
    coverScale: number;
    /** Display-space nudge applied while covering, so the highlighted region stays in frame. */
    offsetX: number;
    offsetY: number;
};

type FramingInput = {
    width: number;
    height: number;
    rotation?: number;
    highlights?: BoundingBox[];
};

/**
 * Frame `image` inside a `tileSize`-square tile. Rotation is applied about the tile's center, so the
 * `<img>` is described in its own PRE-rotation space and its width/height are swapped for a quarter
 * turn — the same coordinate split the app's viewer uses, and the reason the crop math works on
 * `display*` (post-rotation) dimensions throughout.
 */
export function frameThumbnail(image: FramingInput, tileSize: number): ThumbnailFraming {
    const rotationDeg = normalizeRotation(image.rotation);
    const quarterTurned = isQuarterTurn(rotationDeg);

    const displayWidth = quarterTurned ? image.height : image.width;
    const displayHeight = quarterTurned ? image.width : image.height;

    // A doc missing its dimensions can't be framed; fill the tile and let `object-cover` sort it out.
    if (!displayWidth || !displayHeight) {
        return { rotationDeg, width: tileSize, height: tileSize, coverScale: 1, offsetX: 0, offsetY: 0 };
    }

    const aspect = displayWidth / displayHeight;
    const containWidth = aspect >= 1 ? tileSize : tileSize * aspect;
    const containHeight = aspect >= 1 ? tileSize / aspect : tileSize;
    const coverScale = Math.max(aspect, 1 / aspect);

    const { offsetX, offsetY } = coverOffset({
        image,
        rotationDeg,
        tileSize,
        coveredWidth: containWidth * coverScale,
        coveredHeight: containHeight * coverScale,
    });

    return {
        rotationDeg,
        width: quarterTurned ? containHeight : containWidth,
        height: quarterTurned ? containWidth : containHeight,
        coverScale,
        offsetX,
        offsetY,
    };
}

/**
 * Slide the covering image so the union of its highlights sits at the tile's center, clamped so the
 * crop never exposes an edge. No highlights means no nudge — a plain center crop.
 */
function coverOffset({
    image,
    rotationDeg,
    tileSize,
    coveredWidth,
    coveredHeight,
}: {
    image: FramingInput;
    rotationDeg: number;
    tileSize: number;
    coveredWidth: number;
    coveredHeight: number;
}): { offsetX: number; offsetY: number } {
    const focus = highlightUnion(image.highlights);
    if (!focus) return { offsetX: 0, offsetY: 0 };

    const centerX = (focus.x + focus.width / 2) / image.width;
    const centerY = (focus.y + focus.height / 2) / image.height;
    const [displayX, displayY] = rotatePoint(centerX, centerY, rotationDeg);

    // Offset 0 centers the image on the tile, so the nudge is measured from the image's MIDPOINT, and
    // the clamp — half the overhang in either direction — is what keeps an edge from sliding into view.
    return {
        offsetX: clamp(coveredWidth * (0.5 - displayX), (tileSize - coveredWidth) / 2, (coveredWidth - tileSize) / 2),
        offsetY: clamp(
            coveredHeight * (0.5 - displayY),
            (tileSize - coveredHeight) / 2,
            (coveredHeight - tileSize) / 2,
        ),
    };
}

/** The single box covering every highlight, or undefined when there are none. */
function highlightUnion(highlights: BoundingBox[] | undefined): BoundingBox | undefined {
    if (!highlights?.length) return undefined;

    const left = Math.min(...highlights.map((box) => box.x));
    const top = Math.min(...highlights.map((box) => box.y));
    const right = Math.max(...highlights.map((box) => box.x + box.width));
    const bottom = Math.max(...highlights.map((box) => box.y + box.height));

    return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Map a normalized point in the original image onto the same point after a CLOCKWISE rotation. */
function rotatePoint(x: number, y: number, rotationDeg: number): [number, number] {
    switch (rotationDeg) {
        case 90:
            return [1 - y, x];
        case 180:
            return [1 - x, 1 - y];
        case 270:
            return [y, 1 - x];
        default:
            return [x, y];
    }
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}
